import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { formatVndIntegerInput, formatVndIntegerValue, parseVndIntegerInput } from "utils";
import { PayrollDeductionsRemittanceStatementService } from "services/payroll-deductions-remittance-statement";
import { getPrintTemplate } from "./components/printTemplate";

const createRow = () => ({
  contributionMonth: "",
  totalSalaryFund: "",
  insuranceTotal: "",
  contributionToExpense: "",
  deductionFromSalary: "",
  unionFundTotal: "",
  unionFundPayable: "",
  unionFundRetained: "",
});

const numberColumns = [
  "totalSalaryFund",
  "insuranceTotal",
  "contributionToExpense",
  "deductionFromSalary",
  "unionFundTotal",
  "unionFundPayable",
  "unionFundRetained",
];

const parseMoney = (value) => {
  const amount = parseVndIntegerInput(value);
  return Number.isFinite(amount) ? Math.round(amount) : 0;
};

export default function PayrollDeductionsRemittanceStatementDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const today = new Date();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [header, setHeader] = useState({
    unit: "",
    department: "",
    month: String(today.getMonth() + 1).padStart(2, "0"),
    year: String(today.getFullYear()),
    documentNumber: "",
    currencyUnit: "đồng",
  });
  const [rows, setRows] = useState([createRow()]);

  useEffect(() => {
    if (!id) return;

    (async () => {
      try {
        const response = await PayrollDeductionsRemittanceStatementService.getById(id);
        const data = response.data ? response.data : response;

        setHeader({
          unit: data.unit || "",
          department: data.department || "",
          month: String(data.month || "").padStart(2, "0"),
          year: String(data.year || ""),
          documentNumber: data.documentNumber || "",
          currencyUnit: data.currencyUnit || "đồng",
        });

        const mappedRows = (data.rows || []).map((row) => ({
          contributionMonth: row.contributionMonth || "",
          totalSalaryFund: formatVndIntegerInput(row.totalSalaryFund),
          insuranceTotal: formatVndIntegerInput(row.insuranceTotal),
          contributionToExpense: formatVndIntegerInput(row.contributionToExpense),
          deductionFromSalary: formatVndIntegerInput(row.deductionFromSalary),
          unionFundTotal: formatVndIntegerInput(row.unionFundTotal),
          unionFundPayable: formatVndIntegerInput(row.unionFundPayable),
          unionFundRetained: formatVndIntegerInput(row.unionFundRetained),
        }));

        setRows(mappedRows.length ? mappedRows : [createRow()]);
      } catch (error) {
        toast.error("Không thể tải dữ liệu bảng kê trích nộp theo lương");
      }
    })();
  }, [id]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => ({
        totalSalaryFund: acc.totalSalaryFund + parseMoney(row.totalSalaryFund),
        insuranceTotal: acc.insuranceTotal + parseMoney(row.insuranceTotal),
        contributionToExpense: acc.contributionToExpense + parseMoney(row.contributionToExpense),
        deductionFromSalary: acc.deductionFromSalary + parseMoney(row.deductionFromSalary),
        unionFundTotal: acc.unionFundTotal + parseMoney(row.unionFundTotal),
        unionFundPayable: acc.unionFundPayable + parseMoney(row.unionFundPayable),
        unionFundRetained: acc.unionFundRetained + parseMoney(row.unionFundRetained),
      }),
      {
        totalSalaryFund: 0,
        insuranceTotal: 0,
        contributionToExpense: 0,
        deductionFromSalary: 0,
        unionFundTotal: 0,
        unionFundPayable: 0,
        unionFundRetained: 0,
      }
    );
  }, [rows]);

  const setHeaderField = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const setRowField = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      if (numberColumns.includes(field)) {
        next[index] = {
          ...next[index],
          [field]: formatVndIntegerInput(value),
        };
      } else {
        next[index] = { ...next[index], [field]: value };
      }
      return next;
    });
  };

  const handleAddRow = () => {
    setRows((prev) => [...prev, createRow()]);
  };

  const handleRemoveRow = (index) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const buildPayload = () => {
    const unit = String(header.unit || "").trim();
    const department = String(header.department || "").trim();
    const month = String(header.month || "").trim().padStart(2, "0");
    const year = String(header.year || "").trim();
    const documentNumber = String(header.documentNumber || "").trim();
    const currencyUnit = String(header.currencyUnit || "").trim();

    if (!unit) throw new Error("Đơn vị không được để trống");
    if (!department) throw new Error("Bộ phận không được để trống");
    if (!/^\d{1,2}$/.test(month) || Number(month) < 1 || Number(month) > 12) {
      throw new Error("Tháng không hợp lệ (1-12)");
    }
    if (!/^\d{4}$/.test(year)) {
      throw new Error("Năm phải gồm 4 chữ số");
    }
    if (!documentNumber) throw new Error("Số không được để trống");
    if (!currencyUnit) throw new Error("Đơn vị tính không được để trống");

    const normalizedRows = rows
      .map((row) => ({
        contributionMonth: String(row.contributionMonth || "").trim(),
        totalSalaryFund: parseMoney(row.totalSalaryFund),
        insuranceTotal: parseMoney(row.insuranceTotal),
        contributionToExpense: parseMoney(row.contributionToExpense),
        deductionFromSalary: parseMoney(row.deductionFromSalary),
        unionFundTotal: parseMoney(row.unionFundTotal),
        unionFundPayable: parseMoney(row.unionFundPayable),
        unionFundRetained: parseMoney(row.unionFundRetained),
      }))
      .filter((row) => {
        const hasMoney =
          row.totalSalaryFund > 0 ||
          row.insuranceTotal > 0 ||
          row.contributionToExpense > 0 ||
          row.deductionFromSalary > 0 ||
          row.unionFundTotal > 0 ||
          row.unionFundPayable > 0 ||
          row.unionFundRetained > 0;
        return row.contributionMonth || hasMoney;
      });

    if (!normalizedRows.length) {
      throw new Error("Bảng kê phải có ít nhất 1 dòng dữ liệu");
    }

    normalizedRows.forEach((row, index) => {
      const rowNo = index + 1;
      if (!row.contributionMonth) {
        throw new Error(`Dòng ${rowNo}: Số tháng trích không được để trống`);
      }
    });

    return {
      unit,
      department,
      month,
      year,
      documentNumber,
      currencyUnit,
      rows: normalizedRows,
    };
  };

  const handleSubmit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (!id) {
        await PayrollDeductionsRemittanceStatementService.create(payload);
        toast.success("Tạo bảng kê trích nộp theo lương thành công!");
      } else {
        await PayrollDeductionsRemittanceStatementService.update(id, payload);
        toast.success("Cập nhật bảng kê trích nộp theo lương thành công!");
      }
      navigation("/templates/payroll-deductions-remittance-statement");
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Lưu thất bại!");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const htmlContent = getPrintTemplate({
      headerInfo: {
        ...header,
        month: String(header.month || "").replace(/\D/g, "").padStart(2, "0").slice(0, 2),
      },
      rows: rows.map((row) => ({
        ...row,
        totalSalaryFund: parseMoney(row.totalSalaryFund),
        insuranceTotal: parseMoney(row.insuranceTotal),
        contributionToExpense: parseMoney(row.contributionToExpense),
        deductionFromSalary: parseMoney(row.deductionFromSalary),
        unionFundTotal: parseMoney(row.unionFundTotal),
        unionFundPayable: parseMoney(row.unionFundPayable),
        unionFundRetained: parseMoney(row.unionFundRetained),
      })),
      totals,
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "14px",
    },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "14px",
      fontFamily: "inherit",
      backgroundColor: "transparent",
      color: "#000",
    },
    table: { width: "100%", borderCollapse: "collapse", minWidth: 1120, fontSize: 12 },
    th: { border: "1px solid #000", padding: 4, textAlign: "center", fontWeight: "bold", verticalAlign: "middle" },
    td: { border: "1px solid #000", padding: 0, height: "30px" },
    inputText: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "left",
      fontSize: 13,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
    inputCenter: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "center",
      fontSize: 13,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 4px",
      display: "block",
      color: "#000",
    },
    inputNumber: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "right",
      fontSize: 13,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card>
              <SoftBox p={2} style={s.container}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                  <div style={{ width: "50%" }}>
                    <div style={{ fontWeight: "bold", marginBottom: 5 }}>
                      Đơn vị: {" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 230 }}
                        value={header.unit}
                        onChange={(e) => setHeaderField("unit", e.target.value)}
                      />
                    </div>
                    <div style={{ fontWeight: "bold" }}>
                      Bộ phận:{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 230 }}
                        value={header.department}
                        onChange={(e) => setHeaderField("department", e.target.value)}
                      />
                    </div>
                  </div>

                  <div style={{ textAlign: "center", width: "50%" }}>
                    <div style={{ fontWeight: "bold" }}>Mẫu số: 07 - LĐTL</div>
                    <div style={{ fontStyle: "italic", fontSize: "12px", marginTop: 4 }}>
                      (Kèm theo Thông tư số 99/2025/TT-BTC <br /> ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: "center", margin: "10px 0 6px" }}>
                  <h2 style={{ fontSize: 30, fontWeight: "bold", margin: 0, textTransform: "uppercase" }}>
                    BẢNG KÊ TRÍCH NỘP CÁC KHOẢN THEO LƯƠNG
                  </h2>
                  <div style={{ marginTop: 4 }}>
                    Tháng{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 40, textAlign: "center" }}
                      value={header.month}
                      onChange={(e) => setHeaderField("month", e.target.value.replace(/\D/g, "").slice(0, 2))}
                      onBlur={() =>
                        setHeaderField("month", String(header.month || "").replace(/\D/g, "").padStart(2, "0").slice(0, 2))
                      }
                    />{" "}
                    năm{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 70, textAlign: "center" }}
                      value={header.year}
                      onChange={(e) => setHeaderField("year", e.target.value.replace(/\D/g, "").slice(0, 4))}
                    />
                  </div>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 24, marginBottom: 8 }}>
                  <div>
                    Số:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 120 }}
                      value={header.documentNumber}
                      onChange={(e) => setHeaderField("documentNumber", e.target.value)}
                    />
                  </div>
                  <div>
                    Đơn vị tính:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 120 }}
                      value={header.currencyUnit}
                      onChange={(e) => setHeaderField("currencyUnit", e.target.value)}
                    />
                  </div>
                </div>

                <div style={{ marginTop: 8, overflowX: "auto", border: "1px solid #ccc" }}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th} rowSpan={3}>STT</th>
                        <th style={s.th} rowSpan={3}>Số tháng trích BHXH, BHYT, BHTN, KPCĐ</th>
                        <th style={s.th} rowSpan={3}>Tổng quỹ lương trích BHXH, BHYT, BHTN, KPCĐ</th>
                        <th style={s.th} colSpan={3}>BHXH, BHYT, BHTN</th>
                        <th style={s.th} colSpan={3}>KPCĐ</th>
                        {!containView && <th style={s.th} rowSpan={3}>#</th>}
                      </tr>
                      <tr>
                        <th style={s.th} rowSpan={2}>Tổng số</th>
                        <th style={s.th} colSpan={2}>Trong đó:</th>
                        <th style={s.th} rowSpan={2}>Tổng số</th>
                        <th style={s.th} rowSpan={2}>Số phải nộp công đoàn cấp trên</th>
                        <th style={s.th} rowSpan={2}>Số được để lại chi đơn vị</th>
                      </tr>
                      <tr>
                        <th style={s.th}>Trích vào chi phí</th>
                        <th style={s.th}>Trừ vào lương</th>
                      </tr>
                      <tr style={{ backgroundColor: "#fafafa" }}>
                        <th style={s.th}>A</th>
                        <th style={s.th}>B</th>
                        <th style={s.th}>1</th>
                        <th style={s.th}>2</th>
                        <th style={s.th}>3</th>
                        <th style={s.th}>4</th>
                        <th style={s.th}>5</th>
                        <th style={s.th}>6</th>
                        <th style={s.th}>7</th>
                        {!containView && <th style={s.th}></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => (
                        <tr key={`row-${index}`}>
                          <td style={{ ...s.td, textAlign: "center" }}>{index + 1}</td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputCenter}
                              value={row.contributionMonth}
                              onChange={(e) => setRowField(index, "contributionMonth", e.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputNumber}
                              inputMode="numeric"
                              value={row.totalSalaryFund}
                              onChange={(e) => setRowField(index, "totalSalaryFund", e.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputNumber}
                              inputMode="numeric"
                              value={row.insuranceTotal}
                              onChange={(e) => setRowField(index, "insuranceTotal", e.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputNumber}
                              inputMode="numeric"
                              value={row.contributionToExpense}
                              onChange={(e) => setRowField(index, "contributionToExpense", e.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputNumber}
                              inputMode="numeric"
                              value={row.deductionFromSalary}
                              onChange={(e) => setRowField(index, "deductionFromSalary", e.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputNumber}
                              inputMode="numeric"
                              value={row.unionFundTotal}
                              onChange={(e) => setRowField(index, "unionFundTotal", e.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputNumber}
                              inputMode="numeric"
                              value={row.unionFundPayable}
                              onChange={(e) => setRowField(index, "unionFundPayable", e.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputNumber}
                              inputMode="numeric"
                              value={row.unionFundRetained}
                              onChange={(e) => setRowField(index, "unionFundRetained", e.target.value)}
                            />
                          </td>
                          {!containView && (
                            <td style={{ ...s.td, textAlign: "center" }}>
                              <button
                                type="button"
                                style={{ border: "none", background: "transparent", color: "red" }}
                                onClick={() => handleRemoveRow(index)}
                              >
                                X
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}

                      <tr>
                        <td style={{ ...s.td, textAlign: "center" }}></td>
                        <td style={{ ...s.td, textAlign: "center", fontWeight: 700 }}>Cộng</td>
                        <td style={{ ...s.td, textAlign: "right", paddingRight: 6, fontWeight: 700 }}>
                          {formatVndIntegerValue(totals.totalSalaryFund)}
                        </td>
                        <td style={{ ...s.td, textAlign: "right", paddingRight: 6, fontWeight: 700 }}>
                          {formatVndIntegerValue(totals.insuranceTotal)}
                        </td>
                        <td style={{ ...s.td, textAlign: "right", paddingRight: 6, fontWeight: 700 }}>
                          {formatVndIntegerValue(totals.contributionToExpense)}
                        </td>
                        <td style={{ ...s.td, textAlign: "right", paddingRight: 6, fontWeight: 700 }}>
                          {formatVndIntegerValue(totals.deductionFromSalary)}
                        </td>
                        <td style={{ ...s.td, textAlign: "right", paddingRight: 6, fontWeight: 700 }}>
                          {formatVndIntegerValue(totals.unionFundTotal)}
                        </td>
                        <td style={{ ...s.td, textAlign: "right", paddingRight: 6, fontWeight: 700 }}>
                          {formatVndIntegerValue(totals.unionFundPayable)}
                        </td>
                        <td style={{ ...s.td, textAlign: "right", paddingRight: 6, fontWeight: 700 }}>
                          {formatVndIntegerValue(totals.unionFundRetained)}
                        </td>
                        {!containView && <td style={s.td}></td>}
                      </tr>
                    </tbody>
                  </table>
                </div>

                <SoftBox mt={3} display="flex" justifyContent="space-between">
                  <SoftButton variant="gradient" color="light" onClick={() => navigation(-1)}>
                    Trở về
                  </SoftButton>

                  <SoftBox display="flex" gap={2}>
                    {!containView && (
                      <SoftButton variant="outlined" color="dark" onClick={handleAddRow}>
                        THÊM DÒNG
                      </SoftButton>
                    )}
                    {containView && (
                      <SoftButton variant="outlined" color="dark" onClick={handlePrint}>
                        In
                      </SoftButton>
                    )}
                    {!containView && (
                      <SoftButton variant="gradient" color="dark" onClick={handleSubmit} disabled={loadingSubmit}>
                        {id ? "Lưu" : "Tạo"}
                      </SoftButton>
                    )}
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
